// 버벅임 관측 도구 — 사람의 한 동작(액션)이 유발한 API 요청들을 묶어 보여주는 dev 오버레이.
//
// 두 부분으로 나뉜다:
//   1) useActionBoundary — 페이지 이동·클릭을 액션 경계로 삼아 startAction 을 호출(prod 도 동작:
//      이후 나가는 요청에 x-action-id 가 붙어 서버 로그에서 같은 동작으로 묶인다).
//   2) PerfActionOverlay — import.meta.env.DEV 에서만 렌더하는 화면 구석 패널. 각 액션의
//      요청 수·묶음 소요시간·최대 동시 요청 수·총 응답량을 보여준다. 동시>=6(브라우저 연결 한계)이면
//      빨강으로 강조 — "이 화면이 한 번에 너무 많이 쏜다"가 눈에 보이게.

import { useEffect, useLayoutEffect, useReducer, useState } from "react";
import { useLocation } from "react-router";
import { enterRouteRequestScope } from "../../lib/route-request-scope";
import {
  type PerfAction,
  type PerfRequest,
  clearActions,
  getActions,
  startAction,
  subscribe,
} from "../../lib/perf-trace";

/** 페이지 이동·클릭을 액션 경계로 발급한다. BrowserRouter 안에서 한 번 호출한다. */
export function useActionBoundary(): void {
  const loc = useLocation();

  // 페이지 이동 = 새 액션. 같은 경로면 deps 동일이라 재발급 안 함(StrictMode 이중 effect 안전).
  useLayoutEffect(() => {
    // 다음 화면의 useEffect 조회가 시작되기 전에 이전 화면 GET을 먼저 정리한다.
    // 저장·삭제 같은 변경 요청은 공통 API 클라이언트에서 이 신호를 쓰지 않는다.
    enterRouteRequestScope(loc.pathname);
    startAction(`이동 · ${loc.pathname}${loc.search}`);
  }, [loc.pathname, loc.search]);

  // 클릭 = 새 액션. capture 단계라 react-router 의 이동보다 먼저 열린다.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const el = (e.target as HTMLElement | null)?.closest(
        "button, a, [role='button'], [role='tab']",
      );
      const text = el?.textContent?.replace(/\s+/g, " ").trim().slice(0, 24);
      startAction(text ? `클릭 · ${text}` : "클릭");
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);
}

/** BrowserRouter 안에 두는 마운트 지점 — 경계 발급 + (DEV) 오버레이. */
export function ActionBoundary(): React.ReactElement | null {
  useActionBoundary();
  if (!import.meta.env.DEV) return null;
  return <PerfActionOverlay />;
}

/** 한 액션 안 요청들의 최대 동시 실행 수(겹침 구간 스윕). 진행 중 요청은 끝을 무한대로 본다. */
function maxConcurrency(reqs: PerfRequest[]): number {
  const events: Array<[number, number]> = [];
  for (const r of reqs) {
    const end = r.durMs != null ? r.startMs + r.durMs : Number.POSITIVE_INFINITY;
    events.push([r.startMs, 1]);
    events.push([end, -1]);
  }
  events.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  let cur = 0;
  let max = 0;
  for (const [, delta] of events) {
    cur += delta;
    if (cur > max) max = cur;
  }
  return max;
}

/** 액션의 묶음 소요시간(첫 요청 시작 ~ 마지막 응답). 진행 중이면 null(…표시). */
function spanMs(reqs: PerfRequest[]): number | null {
  if (reqs.length === 0) return 0;
  const start = Math.min(...reqs.map((r) => r.startMs));
  let end = -Infinity;
  for (const r of reqs) {
    if (r.durMs == null) return null; // 아직 진행 중
    end = Math.max(end, r.startMs + r.durMs);
  }
  return Math.round(end - start);
}

function fmtBytes(total: number): string {
  if (total <= 0) return "—";
  if (total < 1024) return `${total} B`;
  if (total < 1024 * 1024) return `${(total / 1024).toFixed(1)} KB`;
  return `${(total / (1024 * 1024)).toFixed(2)} MB`;
}

function shortPath(url: string): string {
  try {
    const u = new URL(url, window.location.origin);
    return u.pathname.replace(/^\/api\/v2/, "").replace(/^\/api/, "") + (u.search ? "?…" : "");
  } catch {
    return url;
  }
}

function PerfActionOverlay(): React.ReactElement | null {
  const [, force] = useReducer((x: number) => x + 1, 0);
  const [open, setOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => subscribe(force), []);

  // 요청이 1건 이상인 액션만, 최신순.
  const acts: PerfAction[] = getActions()
    .filter((a) => a.requests.length > 0)
    .slice()
    .reverse();

  const box: React.CSSProperties = {
    position: "fixed",
    right: 12,
    bottom: 12,
    zIndex: 99999,
    fontFamily: "ui-monospace, Menlo, monospace",
    fontSize: 11,
    color: "#e5e7eb",
  };

  if (!open) {
    const last = acts[0];
    const conc = last ? maxConcurrency(last.requests) : 0;
    return (
      <div style={box}>
        <button
          type="button"
          onClick={() => setOpen(true)}
          style={{
            background: conc >= 6 ? "#7f1d1d" : "rgba(17,24,39,0.92)",
            color: "#e5e7eb",
            border: "1px solid #374151",
            borderRadius: 8,
            padding: "6px 10px",
            cursor: "pointer",
          }}
          title="요청 관측 패널 열기"
        >
          ⚡ {last ? `${last.requests.length}req · 동시${conc}` : "관측"}
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        ...box,
        width: 360,
        maxHeight: "70vh",
        overflow: "auto",
        background: "rgba(17,24,39,0.96)",
        border: "1px solid #374151",
        borderRadius: 10,
        padding: 10,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <strong style={{ color: "#93c5fd" }}>⚡ 요청 관측 (액션별)</strong>
        <span>
          <button type="button" onClick={() => clearActions()} style={btn}>비우기</button>
          <button type="button" onClick={() => setOpen(false)} style={btn}>닫기</button>
        </span>
      </div>
      {acts.length === 0 && <div style={{ opacity: 0.6 }}>아직 기록된 요청이 없습니다.</div>}
      {acts.map((a) => {
        const conc = maxConcurrency(a.requests);
        const span = spanMs(a.requests);
        const bytes = a.requests.reduce((s, r) => s + (r.bytes ?? 0), 0);
        const expanded = expandedId === a.id;
        return (
          <div key={a.id} style={{ borderTop: "1px solid #1f2937", padding: "6px 0" }}>
            <div
              style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", gap: 6 }}
              onClick={() => setExpandedId(expanded ? null : a.id)}
            >
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {a.label}
              </span>
              <span style={{ whiteSpace: "nowrap" }}>
                <b>{a.requests.length}</b>req{" "}
                <span style={{ color: conc >= 6 ? "#f87171" : conc >= 3 ? "#fbbf24" : "#9ca3af" }}>
                  동시{conc}
                </span>{" "}
                {span == null ? "…" : `${span}ms`} {fmtBytes(bytes)}
              </span>
            </div>
            {expanded && (
              <div style={{ marginTop: 4, paddingLeft: 6 }}>
                {a.requests.map((r, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 6, opacity: 0.85 }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      <span style={{ color: "#6ee7b7" }}>{r.method}</span> {shortPath(r.url)}
                    </span>
                    <span style={{ whiteSpace: "nowrap", color: r.status && r.status >= 400 ? "#f87171" : r.status == null ? "#fbbf24" : "#9ca3af" }}>
                      {r.status ?? "ERR"} {r.durMs == null ? "…" : `${Math.round(r.durMs)}ms`} {fmtBytes(r.bytes ?? 0)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

const btn: React.CSSProperties = {
  background: "transparent",
  color: "#9ca3af",
  border: "1px solid #374151",
  borderRadius: 6,
  padding: "2px 6px",
  marginLeft: 4,
  cursor: "pointer",
};
